// ResumenProcesosListado.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { RefreshCw, X, Search, FileSpreadsheet } from "lucide-react";
import dayjs from "dayjs";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { DataTable } from "./components/ui/data-table";
import { DateRangePicker } from "./components/ui/date-range-picker";
import { toast } from "./lib/toast";
import { supabase } from "./lib/supabase";

const trimStr = (v) => (typeof v === "string" ? v.trim() : v);
const fmt2 = (n) => (Number.isFinite(n) ? n : 0).toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (p) => Number.isFinite(Number(p)) ? `${Number(p).toFixed(1)} %` : "0.0 %";

const estadoBadge = (v) => {
  const p = String(v ?? "").toLowerCase();
  let variant = "default";
  if (p.includes("final")) variant = "green";
  else if (p.includes("proceso")) variant = "blue";
  else if (p.includes("confirm")) variant = "success";
  return <Badge variant={variant}>{v}</Badge>;
};

// Maps Supabase snake_case row to the camelCase shape used by the table
const normalize = (arr) =>
  (Array.isArray(arr) ? arr : []).map((r, i) => {
    const fecha = r.fecha ? dayjs(r.fecha).format("DD-MM-YYYY") : "";
    const fr = r.fecha_recepcion;
    const fechaRecep = fr
      ? (dayjs(fr, ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"]).isValid()
          ? dayjs(fr, ["DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"]).format("DD-MM-YYYY")
          : String(fr))
      : "";
    const nombre = trimStr(r.nombre);
    const variedad = trimStr(r.variedad);
    const especies = trimStr(r.especies);
    const estado = trimStr(r.estado);
    const numOrden = r.num_orden_packing;
    const kilosEntrada = Number(r.kilos_entrada) || 0;
    const kilosexportados = Number(r.kilos_exportados) || 0;
    const porcentajeExportacion = Number(r.porcentaje_exportacion) || 0;
    const kilosComercial = Number(r.kilos_comercial) || 0;
    const porcentajeComercial = Number(r.porcentaje_comercial) || 0;
    const kilosDesecho = Number(r.kilos_desecho) || 0;
    const porcentajeDesecho = Number(r.porcentaje_desecho) || 0;
    const kilosDeshidratacion = Number(r.kilos_deshidratacion) || 0;
    const porcentajeDeshidratacion = Number(r.porcentaje_deshidratacion) || 0;
    const kilosMerma = Number(r.kilos_merma) || 0;
    const porcentajeMerma = Number(r.porcentaje_merma) || 0;
    const kilosJugo = Number(r.kilos_jugo) || 0;
    const porcentajeJugo = Number(r.porcentaje_jugo) || 0;
    const kilosDDM = Number(r.kilos_ddm) || 0;
    const porcentajeDDM = Number(r.porcentaje_ddm) || 0;
    const kilosTotal = Number(r.kilos_total) || 0;
    const haystack = [fecha, fechaRecep, nombre, variedad, especies, numOrden, estado].join(" ").toLowerCase();
    return {
      key: numOrden ?? i,
      fecha, fechaRecepcion: fechaRecep, numOrden, nombre, variedad, especies,
      kilosEntrada, kilosexportados, porcentajeExportacion,
      kilosComercial, porcentajeComercial,
      kilosDesecho, porcentajeDesecho,
      kilosDeshidratacion, porcentajeDeshidratacion,
      kilosMerma, porcentajeMerma,
      kilosJugo, porcentajeJugo,
      kilosDDM, porcentajeDDM,
      kilosTotal, estado, _haystack: haystack,
    };
  });

const ResumenProcesosListado = () => {
  const [loading, setLoading] = useState(false);
  const [rowsRaw, setRowsRaw] = useState([]);
  const [range, setRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchTextDebounced, setSearchTextDebounced] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  useEffect(() => {
    const t = setTimeout(() => setSearchTextDebounced(searchText.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchText]);

  const fetchResumen = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("resumen_procesos")
        .select("*")
        .order("fecha", { ascending: false });

      if (range && range[0] && range[1]) {
        query = query
          .gte("fecha", dayjs(range[0]).format("YYYY-MM-DD"))
          .lte("fecha", dayjs(range[1]).format("YYYY-MM-DD"));
      }

      const { data, error } = await query;
      if (error) throw error;

      setRowsRaw(normalize(data ?? []));
      setPagination((p) => ({ ...p, current: 1 }));
    } catch (err) {
      console.error(err);
      toast.error("Error al consultar el resumen de procesos.");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchResumen(); }, []); // eslint-disable-line

  const filteredRows = useMemo(() => {
    if (!searchTextDebounced) return rowsRaw;
    return rowsRaw.filter((r) => r._haystack?.includes(searchTextDebounced));
  }, [rowsRaw, searchTextDebounced]);

  const columns = useMemo(() => [
    { title: "Fecha", dataIndex: "fecha", width: 110 },
    { title: "Fecha Recep.", dataIndex: "fechaRecepcion", width: 120 },
    { title: "N° Proceso", dataIndex: "numOrden", width: 110 },
    { title: "Productor", dataIndex: "nombre", ellipsis: true, width: 220 },
    { title: "Especie", dataIndex: "especies", width: 120 },
    { title: "Variedad", dataIndex: "variedad", ellipsis: true, width: 160 },
    { title: "Kg Entrada", dataIndex: "kilosEntrada", align: "right", width: 120, render: (v) => fmt2(v) },
    { title: "Kg Exportados", dataIndex: "kilosexportados", align: "right", width: 130, render: (v) => fmt2(v) },
    { title: "% Exportación", dataIndex: "porcentajeExportacion", align: "right", width: 130, render: (v) => fmtPct(v) },
    { title: "Kg Comercial", dataIndex: "kilosComercial", align: "right", width: 120, render: (v) => fmt2(v) },
    { title: "% Comercial", dataIndex: "porcentajeComercial", align: "right", width: 120, render: (v) => fmtPct(v) },
    { title: "Kg Desecho", dataIndex: "kilosDesecho", align: "right", width: 110, render: (v) => fmt2(v) },
    { title: "% Desecho", dataIndex: "porcentajeDesecho", align: "right", width: 110, render: (v) => fmtPct(v) },
    { title: "Kg Deshidrat.", dataIndex: "kilosDeshidratacion", align: "right", width: 120, render: (v) => fmt2(v) },
    { title: "% Deshidrat.", dataIndex: "porcentajeDeshidratacion", align: "right", width: 120, render: (v) => fmtPct(v) },
    { title: "Kg Merma", dataIndex: "kilosMerma", align: "right", width: 100, render: (v) => fmt2(v) },
    { title: "% Merma", dataIndex: "porcentajeMerma", align: "right", width: 100, render: (v) => fmtPct(v) },
    { title: "Kg Jugo", dataIndex: "kilosJugo", align: "right", width: 100, render: (v) => fmt2(v) },
    { title: "% Jugo", dataIndex: "porcentajeJugo", align: "right", width: 100, render: (v) => fmtPct(v) },
    { title: "Kg DDM", dataIndex: "kilosDDM", align: "right", width: 100, render: (v) => fmt2(v) },
    { title: "% DDM", dataIndex: "porcentajeDDM", align: "right", width: 100, render: (v) => fmtPct(v) },
    { title: "Kg Total", dataIndex: "kilosTotal", align: "right", width: 110, render: (v) => fmt2(v) },
    { title: "Estado", dataIndex: "estado", width: 100, render: (v) => estadoBadge(v) },
  ], []);

  const handleExportExcel = useCallback(async () => {
    try {
      const XLSX = await import("xlsx");
      const data = filteredRows.map((r) => ({
        "Fecha": r.fecha, "Fecha Recepción": r.fechaRecepcion, "N° Proceso": r.numOrden,
        "Productor": r.nombre, "Especie": r.especies, "Variedad": r.variedad,
        "Kg Entrada": r.kilosEntrada, "Kg Exportados": r.kilosexportados, "% Exportación": Number(r.porcentajeExportacion),
        "Kg Comercial": r.kilosComercial, "% Comercial": Number(r.porcentajeComercial),
        "Kg Desecho": r.kilosDesecho, "% Desecho": Number(r.porcentajeDesecho),
        "Kg Deshidratación": r.kilosDeshidratacion, "% Deshidratación": Number(r.porcentajeDeshidratacion),
        "Kg Merma": r.kilosMerma, "% Merma": Number(r.porcentajeMerma),
        "Kg Jugo": r.kilosJugo, "% Jugo": Number(r.porcentajeJugo),
        "Kg DDM": r.kilosDDM, "% DDM": Number(r.porcentajeDDM),
        "Kg Total": r.kilosTotal, "Estado": r.estado,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, ...data.map((row) => (row[h] ? String(row[h]).length : 0))) + 2 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Procesos");
      XLSX.writeFile(wb, `ResumenProcesos_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
    } catch (e) { console.error(e); toast.error("No se pudo exportar a Excel."); }
  }, [filteredRows]);

  const handleLimpiar = () => {
    setRange(null);
    setSearchText("");
    setRowsRaw([]);
    setPagination({ current: 1, pageSize: 20 });
  };

  return (
    <div className="bg-white p-4 rounded-xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 m-0">Resumen de Procesos (Packing)</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Buscar productor / variedad / especie / #proceso / estado"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-80"
            />
            {searchText && (
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchText("")}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <DateRangePicker
            value={range}
            onChange={(vals) => setRange(vals?.[0] && vals?.[1] ? vals : null)}
            format="YYYY-MM-DD"
            allowClear
          />
          <Button onClick={fetchResumen} loading={loading}>
            <RefreshCw className="h-4 w-4" /> Buscar
          </Button>
          <Button variant="outline" onClick={handleLimpiar}>
            <X className="h-4 w-4" /> Limpiar
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </Button>
        </div>
      </div>

      <DataTable
        loading={loading}
        columns={columns}
        data={filteredRows}
        rowKey="key"
        bordered
        scroll={{ x: 2000 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100, 200],
          showTotal: (total, [start, end]) => `${start}-${end} de ${total} registros`,
        }}
        onPaginationChange={(page, pageSize) => setPagination({ current: page, pageSize })}
      />
    </div>
  );
};

export default ResumenProcesosListado;
